import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    Output,
    EventEmitter,
} from "@angular/core";
import { S25IconComponent } from "../modules/s25-icon/s25.icon.component";
import { S25IconButtonComponent } from "./s25.icon.button.component";

import { S25Util } from "../util/s25-util";
import { TypeManagerDecorator } from "../main/type.map.service";
import { DomSanitizer, SafeResourceUrl, SafeHtml } from "@angular/platform-browser";
import { AttachmentService, AttachmentItem } from "../services/attachment.service";
import { S25ModalComponent } from "../modules/s25-modal/s25.modal.component";
import { S25ModalModule } from "../modules/s25-modal/s25.modal.module";

import { SpreadSheetService } from "../modules/s25-spreadsheet/spreadsheet.service";
import Spreadsheet from "../modules/s25-spreadsheet/types/x-spreadsheet.types";
import { WorkBook } from "../modules/s25-spreadsheet/types/sheet.js.types";
import { ScrollLockService } from "../services/scroll.lock.service";
import { S25File } from "../modules/s25-file-upload/s25.file.upload.component";
import { FileStoreUtil } from "../util/filestore.util";
import { RowData, XLSX } from "../modules/s25-spreadsheet/types/sheet.js.types.expanded";

// mammoth .docx to HTML converter
declare global {
    interface Window {
        mammoth: any;
    }
}

@TypeManagerDecorator("s25-ng-display-file")
@Component({
    selector: "s25-ng-display-file",
    template: `
        @if (init) {
            <div class="file-wrapper">
                @if (hasRemoveButton) {
                    <s25-ng-icon-button [type]="'close'" [ariaLabel]="'Remove ' + fileName" (clicked)="removeFile()" />
                }

                <s25-ng-icon [type]="getFileType()" [ariaLabel]="false" />

                @if (!isNewAddFile) {
                    <a
                        class="file-name"
                        target="_self"
                        [href]="getUrl()"
                        [attr.download]="fileName"
                        [attr.title]="fileName"
                    >
                        {{ fileName }}
                    </a>
                } @else {
                    <span class="file-name" [attr.title]="fileName">{{ fileName }}</span>
                }

                @if (getFileType() === "powerPoint" || getFileType() === "visio") {
                    <a target="_self" [href]="getUrl()" [attr.download]="fileName"> Download </a>
                } @else if (!!objectType && !!objectId && !!fileName) {
                    <span class="preview" (click)="openPreviewModal()"> Preview</span>
                }
            </div>

            <s25-ng-modal #previewModal [title]="fileName" [size]="'xl'">
                <ng-template #s25ModalBody>
                    @if (!isOfficeFile()) {
                        @if (getFileType() === "image") {
                            <div class="img-content">
                                <img [src]="safeUrl" [style.max-width]="'600px'" />
                            </div>
                        } @else if (isTextPreviewFile()) {
                            <pre class="docx-content plain-text-preview">{{ plainTextPreview }}</pre>
                        } @else {
                            <iframe [src]="safeUrl" width="100%" height="600px" frameborder="0"></iframe>
                        }
                    } @else {
                        @if (getFileType() === "excel") {
                            <div
                                [style.width]="'fit-content'"
                                class="ngHeight100 c-margin-top--single"
                                #spreadsheetEl
                            ></div>
                        } @else if (isWordDocument()) {
                            <div class="docx-content" [innerHTML]="docxHtml"></div>
                        }
                    }
                </ng-template>
            </s25-ng-modal>
        }
    `,
    host: {
        class: "file-display",
    },
    styles: [
        `
            :host {
                display: block;
                max-width: 100%;
                min-width: 0;
            }

            .preview {
                cursor: pointer;
                color: #2573a7;
            }

            .docx-content {
                margin-top: 20px;
                border: 1px solid #ddd;
                padding: 15px;
                max-height: 600px;
                overflow-y: auto;
            }

            .plain-text-preview {
                white-space: pre-wrap;
                word-break: break-word;
                margin: 0;
            }

            .file-wrapper {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 25px;
                max-width: 100%;
                min-width: 0;
            }

            .file-name {
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                flex: 1 1 auto;
            }

            .file-wrapper > s25-ng-icon,
            .file-wrapper > .preview {
                flex-shrink: 0;
            }

            .img-content {
                min-height: 600px;
                display: flex;
                justify-content: center;
                align-items: center;
            }

            ::ng-deep .nm-party--on .preview,
            ::ng-deep .nm-party--on a {
                color: #73b0e2;
            }

            ::ng-deep .ngEventFormNav {
                z-index: 0;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
    imports: [S25IconComponent, S25IconButtonComponent, S25ModalModule],
})
export class S25DisplayFileComponent implements OnInit {
    @Input() fileName?: string;
    @Input() objectType?: number;
    @Input() objectId?: number;
    @Input() fileKey?: string;
    @Input() hasHyperlink?: boolean;
    @Input() hasRemoveButton?: boolean = false;
    @Input() file?: AttachmentItem | S25File;
    @Input() isNewAddFile?: boolean = false;
    @Input() fileData?: any; // string | ArrayBuffer;
    /**
     * Alternative input: FileStore custom attribute value (e.g., "object_type=2&object_id=43&file_name=Pickles.jpg")
     * If provided, this will be parsed and used instead of individual fileName/objectType/objectId inputs
     */
    @Input() fileStoreUrl?: string;
    @ViewChild("previewModal") previewModal: S25ModalComponent;
    @ViewChild("spreadsheetEl") spreadsheetEl: ElementRef<HTMLDivElement>;
    @Output() onRemove = new EventEmitter();

    baseUrl: string = window.ProData && window.ProData.webservicesBaseUrl;

    static count: number = 0;
    id: string;
    init: boolean = false;
    safeUrl: SafeResourceUrl;

    spreadsheetCreated = false;
    XLSX: XLSX;
    scrollLocksHandled = false;
    spreadsheet: Spreadsheet;

    scriptsPromise?: Promise<void>;

    docxHtml: SafeHtml = "";
    plainTextPreview = "";

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
        this.id = "ngFile-" + S25DisplayFileComponent.count++;
    }

    ngOnInit() {
        // If fileStoreUrl is provided, parse it and set fileName/objectType/objectId
        if (this.fileStoreUrl && !this.fileName) {
            const params = FileStoreUtil.parseFileStoreUrl(this.fileStoreUrl);
            if (params) {
                this.fileName = params.fileName;
                this.objectType = params.objectType;
                this.objectId = params.objectId;
                this.fileKey = params.fileKey;
            }
        }

        this.init = true;
        this.cd.detectChanges();
    }

    getUrl() {
        // https://25live.collegenet.com/25live/data/burnside/run/fs/download/download_file.json?file_name=roseflower.jpg&object_type=3&object_id=1108&display=inline
        return (
            this.baseUrl +
            "/fs/download/download_file.json?file_name=" +
            this.fileName +
            "&object_type=" +
            this.objectType +
            "&object_id=" +
            this.objectId +
            (this.fileKey ? "&file_key=" + encodeURIComponent(this.fileKey) : "")
        );
    }

    getExtension(fileName?: string): string | undefined {
        if (!fileName || fileName === "") return;
        const parts = fileName.split(".");
        return S25Util.toStr(parts[parts.length - 1]).toLowerCase();
    }

    getFileType(): string {
        const ext = this.getExtension(this.fileName);
        return (ext && this.extensionIconMap[ext]) || "generic";
    }

    async openPreviewModal() {
        let view: Blob | undefined;

        if (this.isTextPreviewFile() && !this.isNewAddFile) {
            if (this.objectType == null || this.objectId == null || this.fileName == null || this.fileName === "") {
                return;
            }
            this.previewModal.open();
            view = await AttachmentService.getFile(
                this.objectType,
                this.objectId,
                this.fileName,
                undefined,
                this.fileKey,
            );
            if (view) {
                await this.previewPlainText(view);
            }
            return;
        }

        if (this.isOfficeFile() || this.isNewAddFile) {
            if (!this.isNewAddFile) {
                if (this.objectType == null || this.objectId == null || this.fileName == null || this.fileName === "") {
                    return;
                }
                this.previewModal.open(); // excel file  openModal first then create speadsheet
                view = await AttachmentService.getFile(
                    this.objectType,
                    this.objectId,
                    this.fileName,
                    undefined,
                    this.fileKey,
                );
            } else {
                if (typeof this.fileData === "string" && this.fileData.startsWith("data:")) {
                    const blob = this.toBlobFromBase64(this.fileData);
                    const objectUrl = URL.createObjectURL(blob);
                    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
                    view = blob;
                } else if (this.fileData instanceof Blob || this.fileData instanceof ArrayBuffer) {
                    const blob = new Blob([this.fileData], { type: this.getContentType() });
                    const objectUrl = URL.createObjectURL(blob);
                    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
                    view = blob;
                } else {
                    console.error("Unsupported fileData type");
                }
                if (this.getFileType() !== "excel") this.previewModal.open();
            }

            if (view && this.getFileType() === "excel") {
                this.previewModal.open(); // excel file  openModal first then create speadsheet
                setTimeout(() => {
                    this.previewExcel(view!);
                });
            } else if (view && this.isTextPreviewFile()) {
                await this.previewPlainText(view);
            } else if (view && this.isWordDocument()) {
                await this.previewText(view);
            }
        } else {
            const previewUrl = this.getUrl() + "&display=inline";
            this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
            this.previewModal.open(); // non OfficeFile need to set safe url then openModal
        }
    }

    isOfficeFile(): boolean {
        const ext = this.getExtension(this.fileName);
        return ext ? ["doc", "docx", "xls", "xlsx", "ppt", "pptx", "vsdx"].includes(ext) : false;
    }

    isTextPreviewFile(): boolean {
        const ext = this.getExtension(this.fileName);
        return ext === "txt" || ext === "rtf";
    }

    isWordDocument(): boolean {
        const ext = this.getExtension(this.fileName);
        return ext === "doc" || ext === "docx";
    }

    private extensionIconMap: { [key: string]: string } = {
        jpg: "image",
        jpeg: "image",
        png: "image",
        gif: "image",
        pdf: "pdf",
        doc: "text",
        docx: "text",
        txt: "text",
        rtf: "text",
        xls: "excel",
        xlsx: "excel",
        csv: "excel",
        ppt: "powerPoint",
        pptx: "powerPoint",
        vsdx: "visio",
    };

    async previewExcel(view: Blob) {
        await SpreadSheetService.load();
        this.XLSX = await SpreadSheetService.XLSX();
        this.spreadsheet = window.x_spreadsheet(this.spreadsheetEl.nativeElement, {
            mode: "edit",
            showToolbar: false,
            showGrid: true,
            showContextmenu: true,
            showBottomBar: false,
            view: {
                height: () => 500,
                width: () => 800,
            },
        });
        const arrayBuffer = await view.arrayBuffer();
        const workbook = this.XLSX.read(arrayBuffer);
        await this.processWorkbook(workbook);
    }

    async previewPlainText(view: Blob) {
        let content = await view.text();
        if (this.getExtension(this.fileName) === "rtf") {
            content = this.rtfToPlainText(content);
        }
        this.plainTextPreview = content;
        this.cd.detectChanges();
    }

    private rtfToPlainText(rtf: string): string {
        let text = rtf.replace(/\r\n/g, "\n");
        text = text.replace(/\\par[d]?\s*/gi, "\n");
        text = text.replace(/\\line\s*/gi, "\n");
        text = text.replace(/\\tab\s*/gi, "\t");
        text = text.replace(/\{[^{}]*\}/g, "");
        text = text.replace(/\\[a-z]+-?\d*\s?/gi, "");
        text = text.replace(/[{}]/g, "");
        return text.trim();
    }

    async previewText(view: Blob) {
        await this.loadScripts();
        const arrayBuffer = await view.arrayBuffer();
        const result = await window.mammoth.convertToHtml({ arrayBuffer });
        this.docxHtml = this.sanitizer.bypassSecurityTrustHtml(result.value);
        this.cd.detectChanges();
    }

    getContentType() {
        if (!this.fileData) return undefined;
        const parts = this.fileData.split(",");
        const mime = parts[0].match(/:(.*?);/)?.[1] ?? "";
        return mime;
    }

    processWorkbook = async (workbook: WorkBook) => {
        const xsprData = await SpreadSheetService.stox(workbook);
        this.spreadsheet.loadData(xsprData);
        this.getSheetData();
        this.cd.detectChanges();
    };

    getSheetData = (): RowData[] => {
        if (!this.spreadsheet) {
            return [];
        }
        const rows: RowData[] = [];
        const data = this.spreadsheet.getData();
        if (!data || !data.length || !data[0]) {
            return rows;
        }
        const sheet = data[0];
        if (sheet.rows) {
            for (const rowNum in sheet.rows) {
                const row = sheet.rows[rowNum];
                const rowData: RowData = { cells: [] };
                let cols = 0;
                const cells = rowData.cells ?? [];
                for (const colNum in row.cells ?? {}) {
                    const cell = row.cells[colNum];
                    cols++;
                    cells.push({ text: S25Util.toStr(cell?.text) });
                }
                rowData.cells = cells;
                if (cols <= 0) {
                    break;
                }
                rows.push(rowData);
            }
        }
        return rows;
    };

    handleScrollLock = () => {
        if (this.scrollLocksHandled) {
            return;
        }
        this.scrollLocksHandled = true;

        this.spreadsheetEl.nativeElement.addEventListener("mouseover", () => {
            ScrollLockService.lockBodyScroll();
        });

        this.spreadsheetEl.nativeElement.addEventListener("mouseout", (evt) => {
            if (!this.isEventOverlappingElement(evt, this.spreadsheetEl.nativeElement)) {
                ScrollLockService.unlockBodyScroll();
            }
        });
        document.addEventListener("wheel", this.wheelListener, { passive: false });
    };

    wheelListener = (event: WheelEvent) => {
        if ((event.target as HTMLElement)?.classList?.contains("x-spreadsheet-overlayer")) {
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                event.preventDefault();

                const currentScroll = this.spreadsheet.sheet.horizontalScrollbar.scroll();
                const newLeftPosition = currentScroll.left + event.deltaX;

                this.spreadsheet.sheet.horizontalScrollbar.moveFn(newLeftPosition);
                this.spreadsheet.sheet.horizontalScrollbar.move({ left: newLeftPosition });
            }
        }
    };

    isEventOverlappingElement = (event: MouseEvent, element: HTMLElement) => {
        const rect = element.getBoundingClientRect();
        return (
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom
        );
    };

    async loadScripts(): Promise<void> {
        this.scriptsPromise =
            this.scriptsPromise ||
            S25Util.all([]).then(async () => {
                await S25Util.loadScript("docx-word-js", "https://unpkg.com/mammoth/mammoth.browser.min.js");
                return;
            });
        return this.scriptsPromise;
    }

    async removeFile() {
        try {
            if (this.fileName && this.fileName !== "") {
                this.onRemove.emit(this.file);
            }
        } catch (err) {
            S25Util.showError(err);
        }
        this.cd.detectChanges();
    }

    toBlobFromBase64(base64Data: string): Blob {
        const parts = base64Data.split(",");
        if (parts.length !== 2) {
            throw new Error("Invalid base64 data format");
        }
        const mime = parts[0].match(/:(.*?);/)?.[1] || "application/octet-stream";
        const binaryString = atob(parts[1]);
        const byteArray = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }
        return new Blob([byteArray], { type: mime });
    }
}
